import {observer} from 'mobx-react-lite'
import React, {ReactElement, useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {Lang} from 'yougile-lang'
import {Button, LinkButton} from '@components/common/button/button'
import {TextInput} from '@components/common/text-input/text-input'
import {UsersTable} from '@components/common/users-table/users-table'
import {useGlobalKeyDown} from '@containers/hooks'
import {useHandlers} from './group-chat-handlers'
import {
  GroupChatContext,
  GroupChatStore,
  useGroupChatStore
} from './group-chat-store'

const TwContainer = tw.div`
  p-12
  pb-16
  border
  border-t-0
  border-l-0
  border-r-0
  border-solid
  border-obscure
`

const TwMain = tw.div`
  bg-main-old
  h-full
  w-full
  absolute
  left-0
  top-0
  flex
  flex-col
  z-[53]
`

const TwMainTitle = tw.div`
  mb-16
  font-semibold
  text-18
  leading-main
  text-main-old
`

const TwListTitle = tw.div`
  pt-8
  pb-24
  px-12
  font-semibold
  text-14
  leading-main
`

export const GroupChatEdit = (): ReactElement => {
  const [store] = useState(() => new GroupChatStore())
  return (
    <GroupChatContext.Provider value={store}>
      <GroupChatBase />
    </GroupChatContext.Provider>
  )
}

const GroupChatBase = observer(() => {
  const [, store] = useGroupChatStore()
  useEffect(() => store.syncState(), [])

  // this code throws after each group chat message
  // const open = useSelector((st: AppState) => st.chatPanel.groupChatEdit)
  // useSyncEvent(
  //   'current',
  //   {debounceTime: 500, filter: '*'},
  //   () => {
  //     store.syncState()
  //   },
  //   [open]
  // )

  return (
    <TwMain>
      <Container />
      <Table />
    </TwMain>
  )
})

const Table = observer(() => {
  const [state] = useGroupChatStore()
  const {toggle} = useHandlers()
  return (
    <div style={{height: 'calc(100% - 188px)'}}>
      <TwListTitle>
        {Lang.msg.groupChatEditor.listOfChatParticipants}
      </TwListTitle>
      <UsersTable
        useCheckbox
        className="h-full"
        users={state.users}
        selected={state.chosenUsers}
        onSelectedChanged={toggle}
        showPositionColumn
        disableDefaultSort
      />
    </div>
  )
})

const Container = observer(() => {
  const [state] = useGroupChatStore()
  const {onChange, cancel, save} = useHandlers()
  const inputRef = useRef<HTMLElement>()
  useEffect(() => {
    // autofocus on input messes up transitions; timeout should be greater or
    // equal to right panel slide animation time
    const timeout = setTimeout(() => inputRef.current?.focus(), 300)
    return () => clearTimeout(timeout)
  }, [])

  useGlobalKeyDown({escape: cancel})

  return (
    <TwContainer>
      <TwMainTitle>
        {state.newChat
          ? Lang.msg.groupChatNew
          : Lang.msg.groupChatEditor.editingGroupChat}
      </TwMainTitle>
      <TextInput
        innerRef={inputRef}
        className="mb-8"
        placeholder={Lang.msg.groupChatTitlePlaceholder}
        value={state.title}
        onChange={onChange}
        large
        autoFocus
      />
      <div className="flex gap-16">
        <Button onClick={save}>
          {state.newChat ? Lang.msg.groupChatEditor.createChat : Lang.msg.save}
        </Button>
        <LinkButton className="text-third-old" onClick={cancel}>
          {Lang.msg.cancel}
        </LinkButton>
      </div>
    </TwContainer>
  )
})
